import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, mediaUrl } from "../lib/api";
import Avatar from "../components/Avatar";
import { ShieldCheck, KeyRound } from "lucide-react";

export default function AdminPage() {
  const [key, setKey] = useState(sessionStorage.getItem("egncy_admin_key") || "");
  const [entered, setEntered] = useState(Boolean(key));
  const [requests, setRequests] = useState(null);

  const load = async (k) => {
    try {
      const { data } = await api.get(`/admin/verification-requests?key=${encodeURIComponent(k)}`);
      setRequests(data);
      sessionStorage.setItem("egncy_admin_key", k);
      setEntered(true);
    } catch (err) {
      toast.error("Invalid admin key");
      setEntered(false);
    }
  };

  useEffect(() => {
    if (key) load(key);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const decide = async (req, approve) => {
    await api.post(`/admin/verification-requests/${req.id}/decide?approve=${approve}&key=${encodeURIComponent(key)}`);
    setRequests((rs) => rs.filter((r) => r.id !== req.id));
    toast.success(approve ? `${req.user?.name} is now verified` : "Request rejected");
  };

  if (!entered) {
    return (
      <div className="max-w-md mx-auto px-4 py-16" data-testid="admin-page">
        <h1 className="font-display text-4xl uppercase tracking-tight text-white mb-4 flex items-center gap-3">
          <KeyRound className="w-8 h-8" /> Admin
        </h1>
        <form
          onSubmit={(e) => { e.preventDefault(); load(key); }}
          className="bg-[#121212] border border-[#262626] rounded-sm p-5 space-y-3"
        >
          <input
            data-testid="admin-key-input"
            type="password"
            value={key}
            onChange={(e) => setKey(e.target.value)}
            placeholder="Admin key"
            className="w-full bg-[#0a0a0a] border border-[#262626] focus:border-white outline-none px-4 py-3 rounded-sm text-white"
          />
          <button data-testid="admin-key-submit" className="w-full py-2.5 bg-white text-black font-semibold rounded-sm hover:bg-gray-200 text-sm">
            Unlock
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-4 py-8" data-testid="admin-page">
      <h1 className="font-display text-4xl uppercase tracking-tight text-white mb-1 flex items-center gap-3">
        <ShieldCheck className="w-8 h-8" /> Verification requests
      </h1>
      <p className="text-sm text-[#a3a3a3] mb-6">Approve scouts and coaches to give them the verified badge.</p>

      {requests === null ? (
        <div className="text-[#737373]">Loading…</div>
      ) : requests.length === 0 ? (
        <div data-testid="admin-empty" className="bg-[#121212] border border-[#262626] p-8 rounded-sm text-center text-[#a3a3a3] text-sm">
          No pending requests.
        </div>
      ) : (
        <ul className="space-y-3">
          {requests.map((r) => (
            <li key={r.id} data-testid={`verification-request-${r.id}`} className="bg-[#121212] border border-[#262626] rounded-sm p-5">
              <div className="flex items-center gap-3">
                <Avatar url={r.user?.avatar_url} name={r.user?.name} size={44} />
                <div className="flex-1 min-w-0">
                  <div className="text-white font-semibold">{r.user?.name}</div>
                  <div className="text-xs text-[#a3a3a3]">
                    {r.user?.role} · {r.user?.email}
                  </div>
                </div>
                <span className="text-[10px] uppercase tracking-widest text-[#737373]">
                  {new Date(r.created_at).toLocaleDateString()}
                </span>
              </div>
              <div className="mt-3 text-sm text-white">
                <span className="text-[#737373] text-xs uppercase tracking-widest mr-2">Organization</span>
                {r.organization}
              </div>
              {r.message && <p className="text-sm text-[#a3a3a3] mt-1">“{r.message}”</p>}
              {r.proof_path && (
                <a
                  href={mediaUrl(r.proof_path)}
                  target="_blank"
                  rel="noreferrer"
                  data-testid={`proof-link-${r.id}`}
                  className="inline-block mt-2 text-xs text-white underline"
                >
                  View proof document
                </a>
              )}
              <div className="flex gap-2 mt-4">
                <button
                  data-testid={`approve-${r.id}`}
                  onClick={() => decide(r, true)}
                  className="px-5 py-2 bg-white text-black font-semibold rounded-sm hover:bg-gray-200 text-sm"
                >
                  Approve
                </button>
                <button
                  data-testid={`reject-${r.id}`}
                  onClick={() => decide(r, false)}
                  className="px-5 py-2 border border-[#404040] text-white rounded-sm hover:bg-[#1a1a1a] text-sm"
                >
                  Reject
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
